import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { HelpCenterState, HelpGuide, HelpModule, HelpModuleConfig } from '@/types/help'
import { helpGuides, helpModules } from '@/views/Help/data/guides'

const STORAGE_KEY = 'help-center-state'

// Mapeo de rutas a guías contextuales
// Soporta patrones exactos y con comodines
type RouteGuideMapping = {
  pattern: string | RegExp
  guide?: string // slug de guía específica
  module?: HelpModule // o navegar al módulo
}

const routeToGuideMap: RouteGuideMapping[] = [
  // Dashboard
  { pattern: '/dashboard', module: 'dashboard' },
  { pattern: /^\/dashboard/, guide: 'dashboard-overview' },

  // Proyectos
  { pattern: '/projects', guide: 'projects-list' },
  { pattern: /^\/projects\/[^/]+$/, guide: 'projects-detail' },
  { pattern: /^\/projects\/[^/]+\/epics/, guide: 'epics-overview' },
  { pattern: /^\/projects\/[^/]+\/requirements/, guide: 'requirements-overview' },
  { pattern: /^\/projects\/[^/]+\/documents/, guide: 'documents-create' },
  { pattern: /^\/projects\/[^/]+\/releases/, guide: 'releases-overview' },
  { pattern: /^\/projects\/[^/]+\/meetings/, guide: 'meetings-overview' },

  // Backlog y Sprint
  { pattern: '/backlog', guide: 'backlog-overview' },
  { pattern: /^\/backlog\/sprint/, guide: 'sprint-board-overview' },
  { pattern: /^\/sprint-board/, guide: 'sprint-board-overview' },

  // Requerimientos
  { pattern: /^\/requirements/, guide: 'requirements-overview' },

  // Tickets
  { pattern: '/tickets', guide: 'tickets-create' },
  { pattern: /^\/tickets\//, guide: 'tickets-status' },

  // Documentos
  { pattern: '/documents', guide: 'documents-create' },
  { pattern: /^\/documents\/[^/]+\/edit/, guide: 'documents-create' },
  { pattern: /^\/documents\/[^/]+\/share/, guide: 'documents-share' },

  // Organizaciones
  { pattern: '/organizations', guide: 'organizations-overview' },
  { pattern: /^\/organizations\/[^/]+$/, guide: 'organizations-detail' },

  // CRM
  { pattern: '/crm', guide: 'crm-pipeline' },
  { pattern: '/crm/prospects', guide: 'crm-prospects' },
  { pattern: '/crm/pipeline', guide: 'crm-pipeline' },

  // Calendario
  { pattern: '/calendar', guide: 'calendar-overview' },
  { pattern: /^\/calendar\/appointments/, guide: 'calendar-appointments' },

  // Reuniones
  { pattern: '/meetings', guide: 'meetings-overview' },
  { pattern: /^\/meetings\/[^/]+$/, guide: 'meetings-action-items' },

  // Peticiones
  { pattern: '/petitions', guide: 'petitions-create' },
  { pattern: /^\/petitions\/new/, guide: 'petitions-ai-assistant' },

  // Cotizaciones
  { pattern: '/quotations', guide: 'quotations-create' },
  { pattern: /^\/quotations\/[^/]+$/, guide: 'quotations-status' },

  // Órdenes de venta
  { pattern: '/sales-orders', guide: 'sales-orders-overview' },

  // Perfil
  { pattern: '/profile', guide: 'profile-overview' },
  { pattern: '/settings', guide: 'profile-overview' },

  // Admin
  { pattern: '/admin/users', guide: 'admin-users' },
  { pattern: '/admin/settings', guide: 'admin-settings' },
  { pattern: /^\/admin/, guide: 'admin-users' },

  // Copilot
  { pattern: '/copilot', guide: 'copilot-overview' },

  // Super Admin
  { pattern: '/super-admin/tenants', guide: 'superadmin-tenants' },
  { pattern: '/super-admin/applications', guide: 'superadmin-apps' },
  { pattern: /^\/super-admin/, guide: 'superadmin-tenants' },

  // Releases
  { pattern: '/releases', guide: 'releases-overview' },

  // Épicas
  { pattern: '/epics', guide: 'epics-overview' },
]

function findGuideForRoute(path: string): RouteGuideMapping | null {
  for (const mapping of routeToGuideMap) {
    if (typeof mapping.pattern === 'string') {
      if (path === mapping.pattern || path.startsWith(mapping.pattern + '/')) {
        return mapping
      }
    } else if (mapping.pattern.test(path)) {
      return mapping
    }
  }
  return null
}

function loadState(): HelpCenterState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        ...parsed,
        currentView: parsed.currentView || 'home',
        minimizedAt: parsed.minimizedAt ? new Date(parsed.minimizedAt) : null,
        // Siempre iniciar con hasSeenIntro en false para mostrar la animación
        hasSeenIntro: false,
      }
    }
  } catch {
    /* ignore */
  }
  return {
    isOpen: false,
    isMinimized: false,
    currentGuide: null,
    currentModule: null,
    currentView: 'home',
    searchQuery: '',
    minimizedAt: null,
    hasSeenIntro: false,
  }
}

// Estado singleton
const state = ref<HelpCenterState>(loadState())

function persistState() {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        isOpen: state.value.isOpen,
        isMinimized: state.value.isMinimized,
        currentGuide: state.value.currentGuide,
        currentModule: state.value.currentModule,
        currentView: state.value.currentView,
        searchQuery: '',
        minimizedAt: state.value.minimizedAt?.toISOString() || null,
        // No persistimos hasSeenIntro - siempre se resetea al cargar
      })
    )
  } catch {
    /* ignore */
  }
}

export function useHelpCenter() {
  const authStore = useAuthStore()

  // Filtrar módulos por rol del usuario
  const availableModules = computed<HelpModuleConfig[]>(() => {
    const userRole = authStore.userRole
    if (!userRole) return []

    return helpModules
      .filter((m) => !m.roles || m.roles.includes(userRole))
      .sort((a, b) => a.order - b.order)
  })

  // Filtrar guías por rol del usuario
  const availableGuides = computed<HelpGuide[]>(() => {
    const userRole = authStore.userRole
    if (!userRole) return []

    return helpGuides.filter((g) => !g.roles || g.roles.includes(userRole))
  })

  // Guías del módulo actual
  const currentModuleGuides = computed<HelpGuide[]>(() => {
    if (!state.value.currentModule) return []
    return availableGuides.value
      .filter((g) => g.module === state.value.currentModule)
      .sort((a, b) => a.order - b.order)
  })

  // Guía actual
  const currentGuide = computed<HelpGuide | null>(() => {
    if (!state.value.currentGuide) return null
    return availableGuides.value.find((g) => g.slug === state.value.currentGuide) || null
  })

  // Búsqueda
  const searchResults = computed<HelpGuide[]>(() => {
    const query = state.value.searchQuery.toLowerCase().trim()
    if (!query) return []

    return availableGuides.value
      .filter(
        (g) =>
          g.title.toLowerCase().includes(query) ||
          g.description?.toLowerCase().includes(query) ||
          g.keywords?.some((k) => k.toLowerCase().includes(query)) ||
          g.content.toLowerCase().includes(query)
      )
      .slice(0, 10)
  })

  function open(guideSlug?: string) {
    // Reset flags para mostrar intro al abrir fresh
    state.value.hasSeenIntro = false
    restoredFromMinimized = false

    state.value.isOpen = true
    state.value.isMinimized = false
    if (guideSlug) {
      const guide = availableGuides.value.find((g) => g.slug === guideSlug)
      if (guide) {
        state.value.currentGuide = guideSlug
        state.value.currentModule = guide.module
        // Si se abre con guía específica, saltar intro
        state.value.hasSeenIntro = true
      }
    }
    persistState()
  }

  function close() {
    state.value.isOpen = false
    state.value.isMinimized = false
    state.value.searchQuery = ''
    // Reset para que la próxima apertura muestre intro
    state.value.hasSeenIntro = false
    restoredFromMinimized = false
    persistState()
  }

  function minimize() {
    state.value.isOpen = false
    state.value.isMinimized = true
    state.value.minimizedAt = new Date()
    persistState()
  }

  function restore() {
    // Marcar que viene de restore para NO mostrar intro
    restoredFromMinimized = true
    state.value.hasSeenIntro = true

    state.value.isOpen = true
    state.value.isMinimized = false
    persistState()
  }

  function navigateToGuide(slug: string) {
    const guide = availableGuides.value.find((g) => g.slug === slug)
    if (guide) {
      state.value.currentGuide = slug
      state.value.currentModule = guide.module
      state.value.currentView = 'guide'
      state.value.searchQuery = ''
    }
  }

  function navigateToModule(moduleId: HelpModule) {
    state.value.currentModule = moduleId
    state.value.currentGuide = null
    state.value.currentView = 'module'
    state.value.searchQuery = ''
  }

  function navigateToChangelog() {
    state.value.currentView = 'changelog'
    state.value.currentModule = null
    state.value.currentGuide = null
    state.value.searchQuery = ''
  }

  function navigateToFeedback() {
    state.value.currentView = 'feedback'
    state.value.currentModule = null
    state.value.currentGuide = null
    state.value.searchQuery = ''
  }

  function navigateToNews() {
    state.value.currentView = 'news'
    state.value.currentModule = null
    state.value.currentGuide = null
    state.value.searchQuery = ''
  }

  function setSearchQuery(query: string) {
    state.value.searchQuery = query
  }

  function goHome() {
    state.value.currentModule = null
    state.value.currentGuide = null
    state.value.currentView = 'home'
    state.value.searchQuery = ''
  }

  // Obtener guía sugerida para la ruta actual
  function getSuggestedGuide(path?: string): HelpGuide | null {
    const route = useRoute()
    const currentPath = path || route.path
    const mapping = findGuideForRoute(currentPath)

    if (!mapping) return null

    if (mapping.guide) {
      return availableGuides.value.find((g) => g.slug === mapping.guide) || null
    }

    if (mapping.module) {
      // Retornar primera guía del módulo
      return (
        availableGuides.value
          .filter((g) => g.module === mapping.module)
          .sort((a, b) => a.order - b.order)[0] || null
      )
    }

    return null
  }

  // Abrir con contexto de la ruta actual
  function openContextual(path?: string) {
    const route = useRoute()
    const currentPath = path || route.path
    const mapping = findGuideForRoute(currentPath)

    // Reset flags
    restoredFromMinimized = false

    state.value.isOpen = true
    state.value.isMinimized = false

    if (mapping) {
      // Si hay contexto específico, saltar intro
      state.value.hasSeenIntro = true

      if (mapping.guide) {
        const guide = availableGuides.value.find((g) => g.slug === mapping.guide)
        if (guide) {
          state.value.currentGuide = mapping.guide
          state.value.currentModule = guide.module
          state.value.currentView = 'guide'
        }
      } else if (mapping.module) {
        state.value.currentModule = mapping.module
        state.value.currentGuide = null
        state.value.currentView = 'module'
      }
    } else {
      // Sin contexto, mostrar intro
      state.value.hasSeenIntro = false
    }

    state.value.searchQuery = ''
    persistState()
  }

  // Verificar si hay guía contextual disponible
  function hasContextualHelp(path?: string): boolean {
    const route = useRoute()
    const currentPath = path || route.path
    return findGuideForRoute(currentPath) !== null
  }

  // Obtener módulo sugerido para la ruta actual
  function getSuggestedModule(path?: string): HelpModule | null {
    const route = useRoute()
    const currentPath = path || route.path
    const mapping = findGuideForRoute(currentPath)

    if (!mapping) return null

    if (mapping.module) return mapping.module

    if (mapping.guide) {
      const guide = availableGuides.value.find((g) => g.slug === mapping.guide)
      return guide?.module || null
    }

    return null
  }

  // Flag para saber si viene de restore (no mostrar intro)
  let restoredFromMinimized = false

  // Marcar que el usuario ya vio la intro (para esta sesión de apertura)
  function markIntroSeen() {
    state.value.hasSeenIntro = true
    // No persistimos hasSeenIntro para que siempre se muestre al abrir fresh
  }

  // Verificar si debe mostrar intro (siempre al abrir fresh, nunca al restaurar)
  function shouldShowIntro(): boolean {
    return !state.value.hasSeenIntro && !restoredFromMinimized
  }

  // Reset del flag de intro cuando se cierra
  function resetIntroFlag() {
    state.value.hasSeenIntro = false
    restoredFromMinimized = false
  }

  return {
    state: computed(() => state.value),
    availableModules,
    availableGuides,
    currentModuleGuides,
    currentGuide,
    searchResults,
    open,
    close,
    minimize,
    restore,
    navigateToGuide,
    navigateToModule,
    navigateToChangelog,
    navigateToFeedback,
    navigateToNews,
    setSearchQuery,
    goHome,
    // Contextual helpers
    openContextual,
    getSuggestedGuide,
    getSuggestedModule,
    hasContextualHelp,
    // Intro
    markIntroSeen,
    shouldShowIntro,
  }
}
